// Input del nombre con sugerencias de nombres existentes (por empresa), para
// evitar duplicados. Mismo patrón de combobox que el Item Code del wizard de
// New Item (ItemStep.js): las coincidencias son informativas, no se eligen; la
// que es idéntica se marca EXISTS y la pantalla bloquea el envío. La última
// opción, "Create “…”" (o "Use “…”" al editar), y la tecla Enter confirman lo
// escrito y cierran la lista.
import React, { Component } from 'react';
import { C, INPUT, INPUT_ERR, INPUT_RO } from '../Inventory/NewItemWizard/ui';
import { normName } from './masterDataLogic';

const OPTION_BASE = { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', fontSize: '0.85rem', borderTop: `1px solid ${C.line}`, minHeight: '44px' };

export default class NameAutocomplete extends Component {
    constructor(props) {
        super(props);
        this.state = { open: false, active: -1 };
    }

    componentWillUnmount() { clearTimeout(this._blurTimer); }

    flat() {
        const out = [];
        (this.props.groups || []).forEach((g) => g.matches.forEach((m) => out.push({ group: g, m })));
        return out;
    }

    close = () => this.setState({ open: false, active: -1 });

    onKeyDown = (e) => {
        const n = this.flat().length;
        if (e.key === 'Escape') { if (this.state.open) { e.stopPropagation(); this.close(); } return; }
        if (e.key === 'Enter') { e.preventDefault(); this.close(); return; }
        if (!n) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); this.setState((s) => ({ open: true, active: (s.active + 1) % n })); }
        if (e.key === 'ArrowUp') { e.preventDefault(); this.setState((s) => ({ open: true, active: s.active <= 0 ? n - 1 : s.active - 1 })); }
    };

    render() {
        const { id, value, onChange, error, readOnly, maxLength, placeholder, groups, useLabel } = this.props;
        const { open, active } = this.state;
        const listId = `${id}-list`;
        const typed = normName(value);
        const showList = open && !readOnly && typed.length >= 2;
        const hasAny = (groups || []).some((g) => g.matches.length);
        const hasExact = (groups || []).some((g) => g.matches.some((m) => m.score === 100));
        const typedLabel = String(value || '').trim();
        let idx = -1;
        return (
            <div style={{ position: 'relative' }}>
                <input
                    id={id}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showList}
                    aria-controls={listId}
                    aria-activedescendant={showList && active >= 0 ? `${id}-opt-${active}` : undefined}
                    aria-invalid={error ? 'true' : 'false'}
                    aria-describedby={error ? `${id}-err` : undefined}
                    autoComplete="off"
                    value={value || ''}
                    readOnly={readOnly}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    onChange={(e) => { onChange(e.target.value); this.setState({ open: true, active: -1 }); }}
                    onFocus={() => { clearTimeout(this._blurTimer); if (!readOnly) this.setState({ open: true }); }}
                    onBlur={() => { this._blurTimer = setTimeout(() => this.setState({ open: false, active: -1 }), 120); }}
                    onKeyDown={this.onKeyDown}
                    style={readOnly ? INPUT_RO : error ? INPUT_ERR : INPUT}
                />
                {showList && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30, background: '#FFFFFF', border: `1px solid ${C.lineStrong}`, borderRadius: '10px', boxShadow: '0 10px 30px rgba(15,23,42,0.15)', overflow: 'hidden' }}>
                        <ul id={listId} role="listbox" aria-label="Existing names" style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '280px', overflowY: 'auto' }}>
                            {(groups || []).map((g) => (
                                <li key={g.idCompany} role="presentation">
                                    <div style={{ padding: '0.45rem 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: C.faint, background: '#F8FAFC', borderTop: `1px solid ${C.line}`, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                        Similar names · {g.label}{g.loading ? ' · loading…' : ''}
                                    </div>
                                    {g.error && <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: C.warn }}>{g.error}</div>}
                                    {!g.loading && !g.error && !g.matches.length && <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: C.ok }}>✓ No similar names</div>}
                                    <ul role="group" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                        {g.matches.map((m) => {
                                            idx += 1;
                                            const exact = m.score === 100;
                                            const isActive = idx === active;
                                            return (
                                                <li
                                                    key={m.row.code}
                                                    id={`${id}-opt-${idx}`}
                                                    role="option"
                                                    aria-selected={isActive}
                                                    aria-disabled="true"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    style={{ ...OPTION_BASE, background: exact ? C.errBg : isActive ? '#F1F5F9' : '#FFFFFF' }}
                                                >
                                                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: exact ? C.err : C.faint, flexShrink: 0 }}>{m.row.code}</span>
                                                    <span style={{ color: C.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{m.row.name}</span>
                                                    {exact && <span style={{ fontSize: '0.7rem', color: C.err, fontWeight: 800, flexShrink: 0 }}>EXISTS</span>}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                        {hasExact ? (
                            <div style={{ padding: '0.55rem 0.75rem', fontSize: '0.8rem', color: C.err, fontWeight: 600, borderTop: `1px solid ${C.line}` }}>
                                This name already exists — change it to continue.
                            </div>
                        ) : (
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={this.close}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%', minHeight: '44px', padding: '0.55rem 0.75rem', border: 'none', borderTop: `1px solid ${C.line}`, background: C.infoBg, color: C.primary, fontWeight: 700, fontSize: '0.85rem', textAlign: 'left', cursor: 'pointer' }}
                            >
                                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{useLabel || 'Create'} “{typedLabel}”</span>
                                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: C.faint, flexShrink: 0 }}>Enter ↵</span>
                            </button>
                        )}
                        <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.68rem', color: '#94A3B8', borderTop: `1px solid ${C.line}` }}>
                            {hasAny ? 'Check the similar names before continuing — avoid creating a duplicate.' : 'Compared against the loaded list.'}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
